import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { usePrimaryVault } from '@tokenized/sdk-react-private';
import { useHistory } from 'react-router-dom';
import Button from '#components/Elements/Button/Button';
import Spinner from '#components/Elements/Spinner/Spinner';
import {
  locationToOpenModal,
  useIsShowingFullscreenOverlay,
  locationToShowLockboxDashboard,
  locationToShowLockboxStatements,
} from '#app/containers/routes';
import * as modalRoutes from '#app/containers/modalRoutes';
import InstrumentAmount from '#components/Elements/InstrumentAmount/InstrumentAmount';
import DotsMenuIcon from '#components/Elements/SVGIcon/DotsMenuIcon';
import Dropdown from '#components/Elements/Dropdown/Dropdown';
import { EntityContext } from '#modules/context/EntityContext';
import { useAssetsTotal } from '#modules/hooks/assets';
import { useSelectedProfileId } from '#modules/state/ProfileState';
import { isLiquidNobleLoggedIn } from '#modules/utils/jwt';
import styles from './LockboxesTable.scss';

export const LOCKBOX_TYPE_TRANSACTION = 1;
export const LOCKBOX_TYPE_SAVER = 2;

export function LockboxTotalAmount({ lockboxId, instruments, className }) {
  const profileId = useSelectedProfileId();

  const vaultId = usePrimaryVault(profileId)?.id;

  const holdingsTotalValue = useAssetsTotal(
    vaultId,
    profileId,
    instruments,
    lockboxId,
  );
  if (holdingsTotalValue === null) {
    return (
      <div className={styles.smallSpinner}>
        <Spinner />
      </div>
    );
  }
  return (
    <InstrumentAmount
      className={classNames(styles.amount, className)}
      instrument={holdingsTotalValue}
      showDisplayCurrency
      increasePrecisionForTinyNumbers
    />
  );
}

function LockboxesTable({
  loading,
  lockboxesList,
  instruments,
  onRefetchLockboxes,
}) {
  const isLqn = isLiquidNobleLoggedIn();
  const history = useHistory();
  const overlayVisible = useIsShowingFullscreenOverlay();
  const { setActiveLockboxId } = React.useContext(EntityContext);

  const transferInstruments = (lockboxId) =>
    history.push(
      locationToOpenModal(location, modalRoutes.WALLET_TRANSFER, null, {
        id: lockboxId,
      }),
    );

  const deleteLockbox = React.useCallback(
    (lockboxId) =>
      history.push(
        locationToOpenModal(location, modalRoutes.WALLET_DELETE_LOCKBOX, null, {
          id: lockboxId,
        }),
      ),
    [history],
  );

  const showLockboxDashboard = (lockboxId) => {
    if (lockboxId) {
      setActiveLockboxId(lockboxId);
      history.push(locationToShowLockboxDashboard(location, lockboxId));
    }
  };

  const showLockboxStatements = (lockboxId) => {
    if (lockboxId) {
      setActiveLockboxId(lockboxId);
      history.push(locationToShowLockboxStatements(location, lockboxId));
    }
  };

  // Sort lockboxesList by created_at (unix timestamp) - oldest first
  lockboxesList?.sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at),
  );

  return (
    <div className={styles.LockboxesTable}>
      {!loading && (
        <div className={styles.tableBody}>
          {!lockboxesList?.length && !loading && (
            <div className={styles.emptyTableTitle}>
              <FormattedMessage
                defaultMessage="No lockboxes"
                description="No lockboxes"
              />
            </div>
          )}
          {lockboxesList?.map((lockbox) => (
            <div
              key={lockbox.id}
              className={styles.tableRow}
              onClick={() => showLockboxDashboard(lockbox.id)}
              onKeyUp={() => showLockboxDashboard(lockbox.id)}
              tabIndex={0}
              role="button"
            >
              <div className={styles.row}>
                <div className={styles.leftColumn}>
                  <div className={styles.lockboxTitle}>{lockbox.name}</div>
                  <div className={styles.lockboxSubtitle}>
                    {lockbox.lockbox_type === LOCKBOX_TYPE_TRANSACTION ? (
                      <FormattedMessage
                        defaultMessage="Transactional"
                        description="Transactional lockbox type"
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage="Saver"
                        description="Saver lockbox type"
                      />
                    )}
                  </div>
                </div>
                <div className={styles.rightColumn}>
                  <span>
                    <Dropdown
                      className={classNames(!overlayVisible && 'no-drag')}
                      buttonlike
                      align="right"
                      value={0}
                      triggerComponent={
                        <Button icon>
                          <DotsMenuIcon />
                        </Button>
                      }
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isLqn && (
                        <Dropdown.Item
                          value={lockbox.id}
                          onClick={() => transferInstruments(lockbox.id)}
                        >
                          <FormattedMessage
                            defaultMessage="Transfer"
                            description="Transfer lockbox menu action"
                          />
                        </Dropdown.Item>
                      )}
                      <Dropdown.Item
                        value={lockbox.id}
                        onClick={() => showLockboxStatements(lockbox.id)}
                      >
                        <FormattedMessage
                          defaultMessage="View statements"
                          description="View statements lockbox menu action"
                        />
                      </Dropdown.Item>
                      {!lockbox.is_primary && (
                        <Dropdown.Item
                          value={lockbox.id}
                          onClick={() => deleteLockbox(lockbox.id)}
                        >
                          <FormattedMessage
                            defaultMessage="Delete"
                            description="Delete lockbox menu action"
                          />
                        </Dropdown.Item>
                      )}
                    </Dropdown>
                  </span>
                </div>
              </div>
              <div className={classNames(styles.row, styles.lockboxBalanceRow)}>
                <div className={styles.lockboxBalanceLabel}>Balance:</div>
                <div className={styles.lockboxBalance}>
                  <LockboxTotalAmount
                    lockboxId={lockbox.id}
                    instruments={instruments}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && lockboxesList === null && (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      )}
    </div>
  );
}

export default LockboxesTable;
